<template>
<m-pageframe
    title="Separate Customers and Prospects"
>
<v-container
    fluid
    class="pa-4 pa-sm-6"
    :class="$vuetify.breakpoint.mdAndUp ? 'max-width-1200' : ''"
>
    <!-- Page Header -->
    <div class="mb-4">
        <h1 class="text-h4 font-weight-medium mb-2">Separate Customers and Prospects</h1>
        <p class="text-subtitle-1 grey--text text--darken-1">
            Upload an Excel file containing a worksheet with one or more email columns.
            The system will separate the data into customers (active or archived RedChirp users)
            and prospects (non-users) based on email addresses and domains.
        </p>
    </div>

    <!-- How It Works Section -->
    <v-card class="mb-4" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-information</v-icon>
            How It Works
        </v-card-title>
        <v-card-text class="pt-3 pb-3">
            <v-row dense>
                <!-- Input Requirements -->
                <v-col cols="12" lg="3">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-file-upload</v-icon>
                            <span class="subtitle-2 font-weight-medium">Input Requirements</span>
                        </div>
                        <v-list dense class="pa-0">
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Excel file with email column(s)</v-list-item-title>
                                    <v-list-item-subtitle class="caption">Column name must contain "email" in it</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">.xlsx format, max 100MB</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>
                </v-col>

                <!-- Output Information -->
                <v-col cols="12" lg="3">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-file-download</v-icon>
                            <span class="subtitle-2 font-weight-medium">Output</span>
                        </div>
                        <v-list dense class="pa-0">
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="primary" size="16">mdi-account-group</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Customers worksheet</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="secondary" size="16">mdi-account-plus</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Prospects worksheet</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>
                </v-col>

                <!-- Customer Identification -->
                <v-col cols="12" lg="6">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-account-search</v-icon>
                            <span class="subtitle-2 font-weight-medium">Customer Identification</span>
                        </div>
                        <v-list dense class="pa-0">
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-email-check</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Email matches RedChirp username</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-domain</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Email domain matches a custom domain used by a RedChirp account</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <!-- Supported Email Formats Section -->
    <v-card class="mb-4" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-email-variant</v-icon>
            Supported Email Formats
        </v-card-title>
        <v-card-text class="pt-3 pb-3">
            <p class="text-body-2 grey--text text--darken-1 mb-3">
                The system can process various email formats in your Excel columns:
            </p>
            <v-row dense>
                <v-col cols="12" md="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-email</v-icon>
                            <span class="subtitle-2 font-weight-medium">Simple Emails</span>
                        </div>
                        <v-card flat color="grey lighten-5" class="pa-2">
                            <code class="text-body-2">user@domain.com</code>
                        </v-card>
                    </div>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-account-circle</v-icon>
                            <span class="subtitle-2 font-weight-medium">Display Names</span>
                        </div>
                        <v-card flat color="grey lighten-5" class="pa-2">
                            <code class="text-body-2">Jim Kane &lt;jim.kane@gmail.com&gt;</code>
                        </v-card>
                    </div>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-format-list-bulleted</v-icon>
                            <span class="subtitle-2 font-weight-medium">Multiple Emails</span>
                        </div>
                        <v-card flat color="grey lighten-5" class="pa-2">
                            <code class="text-body-2">user1@domain.com, user2@domain.com</code>
                            <br>
                            <code class="text-body-2">user1@domain.com; user2@domain.com</code>
                        </v-card>
                    </div>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <!-- Upload File Section -->
    <v-card class="mb-4" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-cloud-upload</v-icon>
            Upload Excel File
        </v-card-title>
        <v-card-text class="pt-3">
            <p class="text-body-2 grey--text text--darken-1 mb-3">
                Select an .xlsx file containing email addresses to process
            </p>

            <v-file-input
                v-model="selected_file"
                label="Select Excel File"
                accept=".xlsx"
                prepend-icon="mdi-file-excel"
                :disabled="processing"
                @change="onFileSelected"
                outlined
                hide-details="auto"
                class="mb-3"
            />

            <div v-if="selected_file" class="mb-3">
                <v-chip color="success" text-color="white" class="mr-2">
                    <v-icon left small>mdi-file-excel</v-icon>
                    {{ selected_file.name }}
                </v-chip>
                <span class="caption grey--text">{{ formatFileSize(selected_file.size) }}</span>
            </div>

            <v-fade-transition>
                <v-alert
                    v-if="file_error"
                    type="error"
                    class="mb-3"
                    dense
                    outlined
                >
                    {{ file_error }}
                </v-alert>
            </v-fade-transition>

            <v-fade-transition>
                <v-alert
                    v-if="file_success && !selected_file"
                    type="success"
                    class="mb-3"
                    dense
                    outlined
                >
                    {{ file_success }}
                </v-alert>
            </v-fade-transition>

            <!-- Sheet Selection -->
            <v-fade-transition>
                <div v-if="show_sheet_selection" class="mt-4">
                    <v-divider class="mb-4"></v-divider>
                    <div class="d-flex align-center mb-3">
                        <v-icon color="primary" class="mr-2">mdi-table</v-icon>
                        <span class="subtitle-2 font-weight-medium">Select Sheet to Process</span>
                    </div>
                    <p class="text-body-2 grey--text text--darken-1 mb-3">
                        The selected sheet will be processed for customer/prospect separation
                    </p>
                    <v-select
                        v-model="selected_sheet"
                        :items="available_sheets"
                        label="Choose worksheet"
                        prepend-icon="mdi-table"
                        :disabled="processing"
                        outlined
                        hide-details
                    />
                </div>
            </v-fade-transition>

            <div class="d-flex justify-end mt-4">
                <v-btn
                    v-if="!show_sheet_selection"
                    color="primary"
                    :disabled="!selected_file || processing"
                    :loading="processing"
                    @click="onProcessFile"
                    large
                >
                    <v-icon left>mdi-upload</v-icon>
                    Upload File
                </v-btn>
                <v-btn
                    v-if="show_sheet_selection"
                    color="primary"
                    :disabled="!selected_sheet || processing"
                    :loading="processing"
                    @click="onProcessWithSheet"
                    large
                >
                    <v-icon left>mdi-cog</v-icon>
                    Process Selected Sheet
                </v-btn>
            </div>
        </v-card-text>
    </v-card>

    <!-- Processing Status Section -->
    <v-fade-transition>
        <v-card
            v-if="processing_status"
            class="mb-4"
            outlined
        >
            <v-card-title class="grey lighten-4">
                <v-icon
                    left
                    :color="processing_status.error ? 'error' : processing ? 'warning' : 'success'"
                >
                    {{ processing_status.error ? 'mdi-alert-circle' : processing ? 'mdi-cog' : 'mdi-check-circle' }}
                </v-icon>
                {{ processing ? 'Processing File...' : processing_status.error ? 'Processing Failed' : 'Processing Complete' }}
            </v-card-title>
            <v-card-text class="pt-4">
                <p class="text-body-2 grey--text text--darken-1 mb-4">
                    {{ processing ? 'Analyzing email addresses and separating data' : processing_status.error ? 'An error occurred during processing' : 'File has been successfully processed' }}
                </p>

                <!-- Progress Bar -->
                <v-progress-linear
                    v-if="processing"
                    indeterminate
                    color="primary"
                    class="mb-4"
                    height="6"
                    rounded
                />

                <!-- Results Summary -->
                <div v-if="processing_status.total_rows && !processing_status.error">
                    <v-row class="mb-4">
                        <v-col cols="12" sm="4">
                            <v-card outlined class="text-center pa-3">
                                <v-icon color="info" size="32" class="mb-2">mdi-file-table</v-icon>
                                <div class="text-h6 font-weight-medium">{{ processing_status.total_rows }}</div>
                                <div class="caption grey--text">Total Rows</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-card outlined class="text-center pa-3">
                                <v-icon color="primary" size="32" class="mb-2">mdi-account-group</v-icon>
                                <div class="text-h6 font-weight-medium">{{ processing_status.customer_count }}</div>
                                <div class="caption grey--text">Customers</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-card outlined class="text-center pa-3">
                                <v-icon color="secondary" size="32" class="mb-2">mdi-account-plus</v-icon>
                                <div class="text-h6 font-weight-medium">{{ processing_status.prospect_count }}</div>
                                <div class="caption grey--text">Prospects</div>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <!-- Error Alert -->
                <v-fade-transition>
                    <v-alert
                        v-if="processing_status.error"
                        type="error"
                        outlined
                        dense
                        class="mb-4"
                    >
                        {{ processing_status.error }}
                    </v-alert>
                </v-fade-transition>

                <!-- Download Action -->
                <div v-if="processing_status.download_url" class="d-flex justify-end">
                    <v-btn
                        :href="processing_status.download_url"
                        download
                        color="primary"
                        large
                    >
                        <v-icon left>mdi-download</v-icon>
                        Download Separated File
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>
    </v-fade-transition>

</v-container>
</m-pageframe>
</template>

<script>
export default {
    data() {
        return {
            selected_file: null,
            processing: false,
            file_error: null,
            file_success: null,
            processing_status: null,
            uploaded_blob_id: null,
            available_sheets: [],
            selected_sheet: null,
            show_sheet_selection: false,
        }
    },

    methods: {
        onFileSelected() {
            this.file_error = null;
            this.file_success = null;
            this.processing_status = null;
            this.show_sheet_selection = false;
            this.available_sheets = [];
            this.selected_sheet = null;
            this.uploaded_blob_id = null;
            
            if (this.selected_file) {
                // Validate file type
                const allowed_types = [
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // This is for .xlsx files
                ];

                if (!allowed_types.includes(this.selected_file.type)) {
                    this.file_error = 'Please select a valid Excel file (.xlsx).'; 
                    this.selected_file = null;
                    return;
                }

                // Validate file size (100MB limit)
                const max_size = 100 * 1024 * 1024;
                if (this.selected_file.size > max_size) {
                    this.file_error = 'File size must be less than 100MB.';
                    this.selected_file = null;
                    return;
                }

                this.file_success = `Selected: ${this.selected_file.name} (${this.formatFileSize(this.selected_file.size)})`;
            }
        },

        onProcessFile() {
            if (!this.selected_file) {
                this.file_error = 'Please select a file first';
                return;
            }

            this.processing = true;
            this.file_error = null;
            // Don't set processing_status here - only set it when actual processing begins

            // Step 1: Upload the file
            this.uploadFile();
        },

        uploadFile() {
            // Create form data for file upload
            const form_data = new FormData();
            form_data.append('excel_file', this.selected_file);
            form_data.append('label', this.selected_file.name);

            this.$rc.makeApiCall({
                url: 'admin/upload-customer-prospect-file',
                method: 'post',
                form_data: form_data,
                timeout: 10 * 60 * 1000, // 10 minutes for upload
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: this.onUploadSuccess,
                    on_error: this.onProcessError
                }
            });
        },

        onUploadSuccess(response) {

            // Store the blob ID and sheet names
            this.uploaded_blob_id = response.blob_id;
            this.available_sheets = response.sheet_names || [];

            if (this.available_sheets.length === 0) {
                this.onProcessError({ message: 'No sheets found in the Excel file' });
                return;
            }

            // If only one sheet, auto-select it and process immediately
            if (this.available_sheets.length === 1) {
                this.selected_sheet = this.available_sheets[0];
                // Set processing_status when actual processing begins
                this.processing_status = {
                    total_rows: 0,
                    customer_count: 0,
                    prospect_count: 0,
                    error: null,
                    download_url: null
                };
                this.processWithSelectedSheet();
            } else {
                // Show sheet selection UI
                this.processing = false;
                this.show_sheet_selection = true;
                this.selected_sheet = this.available_sheets[0]; // Default to first sheet
            }
        },

        onProcessWithSheet() {
            if (!this.selected_sheet) {
                this.file_error = 'Please select a sheet to process';
                return;
            }

            this.processing = true;
            this.file_error = null;
            // Set processing_status when actual processing begins
            this.processing_status = {
                total_rows: 0,
                customer_count: 0,
                prospect_count: 0,
                error: null,
                download_url: null
            };
            this.processWithSelectedSheet();
        },

        processWithSelectedSheet() {
            const request_body = {
                blob_id: this.uploaded_blob_id,
                sheet_name: this.selected_sheet
            };


            this.$rc.makeApiCall({
                url: 'employees/separate-customers-prospects',
                method: 'post',
                data: request_body,
                timeout: 5 * 60 * 1000, // 5 minutes for processing
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: this.onProcessSuccess,
                    on_error: this.onProcessError
                }
            });
        },

        onProcessSuccess(response) {
            this.processing = false;
            this.processing_status = {
                total_rows: response.total_rows,
                customer_count: response.customer_count,
                prospect_count: response.prospect_count,
                error: null,
                download_url: response.download_url
            };
        },

        onProcessError(error) {
            this.processing = false;
            this.processing_status = {
                total_rows: 0,
                customer_count: 0,
                prospect_count: 0,
                error: error.message || 'An error occurred while processing the file',
                download_url: null
            };
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        }
    }
}
</script>

<style scoped>
/* Container max width */
.max-width-1200 {
    max-width: 1200px;
    margin: 0 auto;
}

/* Ensure consistent spacing */
.v-card {
    margin-bottom: 16px;
}

/* Better list styling */
.v-list-item__icon {
    margin-right: 12px !important;
    min-width: 32px !important;
}

/* Smooth transitions */
.v-btn {
    transition: all 0.3s ease;
}

/* Ensure proper spacing in nested cards */
.v-list-item {
    min-height: auto !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
}
</style>
